import {
  Box,
  Button,
  ErrorBoundary,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack,
} from '@genial/components';
import { BoxBorder, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useMemo } from 'react';

import { useRecoilQuery } from '../../../../../hooks/useRecoilQuery';
import { fixedFeeTermHistoryQuery } from '../../../../../store/fixedFee/selector';
import { FixedFeeTermHistoryItem } from '../../../../../store/fixedFee/types';
import { StatusTimeline, StatusTimelineLoading } from '../StatusTimeline';

export interface TermHistoryContentProps {
  termId: string;
  term: string;
  customerName: string;
  customerCustodyAccount: string;
  status: string;
  history?: FixedFeeTermHistoryItem[];
  onClose: () => void;
}

export const TermHistoryContent: React.FC<TermHistoryContentProps> = ({
  termId,
  term,
  customerName,
  customerCustodyAccount,
  status,
  history,
  onClose,
}) => {
  const { data, error, loading, refresher } = useRecoilQuery(fixedFeeTermHistoryQuery({ termId }), {
    if: () => !history,
  });

  const historyData = history || data?.data || [];
  const rowsCount = useMemo(() => Math.floor(Math.random() * 4 + 2), []);

  const renderContent = () => {
    if (error) {
      return <ErrorBoundary onRefresh={refresher} />;
    }

    if (loading) {
      return <StatusTimelineLoading rowsCount={rowsCount} />;
    }

    return <StatusTimeline items={historyData} />;
  };

  return (
    <VStack
      flex={1}
      padding="inline.xxs"
      space="stack.xxxs"
      bgColor="neutral.white"
      overflowY="scroll">
      <HStack w="full" justifyContent="space-between" alignItems="center">
        <Button
          p={0}
          variant="textLink"
          leftIcon={<Icon name="ArrowLeft" size="xxs" color="neutral.black" />}
          onPress={onClose}
          _text={{
            textTransform: 'none',
            color: 'neutral.black',
            fontSize: Typography['fontsize.lg'],
          }}>
          Detalhes do cliente
        </Button>
        <IconButton
          testId="close-fixed-fee-history-modal"
          p={0}
          icon="CloseModal"
          iconSize="xxs"
          borderWidth="1"
          borderColor="brand.primary.principal"
          iconColor="brand.primary.principal"
          onPress={onClose}
        />
      </HStack>

      <VStack
        bgColor={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.3)}
        borderRadius={BoxBorder.heavy}
        p="inset.xs"
        space="stack.nano">
        <HStack justifyContent="space-between">
          <Text>Nome do cliente</Text>
          <Text variant="semibold" color="neutral.black">
            {customerName}
          </Text>
        </HStack>

        <HStack justifyContent="space-between">
          <Text>Conta de custódia</Text>
          <Text color="neutral.darkest" variant="semibold">
            {customerCustodyAccount}
          </Text>
        </HStack>

        <HStack justifyContent="space-between">
          <Text>Termo</Text>
          <Text color="neutral.darkest" variant="semibold">
            {term}
          </Text>
        </HStack>

        <HStack justifyContent="space-between">
          <Text>Status</Text>
          <Text color="brand.primary.principal" variant="semibold">
            {status}
          </Text>
        </HStack>
      </VStack>

      <Box>
        <Text variant="semibold" fontSize="lg" color="neutral.black">
          Acompanhe o processo
        </Text>
      </Box>

      {renderContent()}
    </VStack>
  );
};
